import { useTranslation, Trans } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { GoogleIcon } from './google-icon'
import { Star, TrendingUp, MessageSquare, ChevronRight, Shield, AlertCircle } from 'lucide-react'

interface OnboardingWelcomeProps {
  onConnectGoogle: () => void
  onSkip: () => void
}

export function OnboardingWelcome({ onConnectGoogle, onSkip }: OnboardingWelcomeProps) {
  const { t } = useTranslation()

  const features = [
    {
      icon: Star,
      title: t('onboarding.featureTrackReviewsTitle'),
      description: t('onboarding.featureTrackReviewsDesc'),
    },
    {
      icon: TrendingUp,
      title: t('onboarding.featureGrowRatingsTitle'),
      description: t('onboarding.featureGrowRatingsDesc'),
    },
    {
      icon: MessageSquare,
      title: t('onboarding.featureRespondFastTitle'),
      description: t('onboarding.featureRespondFastDesc'),
    },
  ]

  const googleBenefits = [
    t('onboarding.benefitRealtimeSync'),
    t('onboarding.benefitRespondDirectly'),
    t('onboarding.benefitVerifiedData'),
    t('onboarding.benefitFullAnalytics'),
  ]

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Branding (desktop only) */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between items-start">
          <div /> {/* Spacer */}
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight text-balance">
              {t('onboarding.welcomeHeroTitle')}
            </h1>
            <p className="text-lg text-white/80 max-w-md">{t('onboarding.welcomeHeroSubtitle')}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-white text-sm font-medium"
                >
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <p className="text-white/80 text-sm">
              <Trans
                i18nKey="onboarding.businessesOnboard"
                components={{ strong: <span className="font-semibold text-white" /> }}
              />
            </p>
          </div>
        </div>

        {/* Right Panel - Action */}
        <div className="flex-1 flex flex-col justify-center p-8 lg:p-12 overflow-y-auto">
          <div className="max-w-md mx-auto w-full space-y-8">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-gray-900">{t('onboarding.welcomeTitle')}</h2>
              <p className="text-gray-500">{t('onboarding.welcomeSubtitle')}</p>
            </div>

            {/* Features */}
            <div className="grid gap-4">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white border border-border"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{feature.title}</h3>
                    <p className="text-sm text-gray-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-accent" />
                <span className="text-sm font-medium text-accent">
                  {t('onboarding.recommended')}
                </span>
              </div>
              <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                <p className="text-sm font-medium text-gray-900 mb-3">
                  {t('onboarding.benefitsHeading')}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {googleBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button
                onClick={onConnectGoogle}
                size="lg"
                className="w-full h-14 text-base font-semibold gap-3 group"
              >
                <GoogleIcon className="w-5 h-5" />
                {t('onboarding.connectGoogle')}
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-warm-gray px-2 text-gray-400">{t('onboarding.or')}</span>
                </div>
              </div>

              <button
                onClick={onSkip}
                className="w-full py-3 text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                {t('onboarding.continueSavoriasOnly')}
              </button>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <Trans
                    i18nKey="onboarding.limitedFeaturesWarning"
                    components={{ strong: <span className="font-medium" /> }}
                  />
                </p>
              </div>

              <p className="text-xs text-center text-gray-400">
                <Trans
                  i18nKey="onboarding.continueLegal"
                  components={{
                    terms: <a href="#" className="underline hover:text-gray-700" />,
                    privacy: <a href="#" className="underline hover:text-gray-700" />,
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
